import { AssignmentListItem } from 'app/models/assignment-list-params'
import { createReducer } from 'app/state/utils'
import { acceptedIds, invalidate, oneId, outdated, withRows } from 'app/state/utils/generation'
import * as assignmentTypes from 'app/state/ducks/assignments/types'
import * as types from './types'

export interface AssignmentListState {
  rows: AssignmentListItem[]
  isLoading: boolean
  /**
   * True once a write has happened that the rows cannot be patched for, or a
   * read came back from before a write. The thunk reads once more for the
   * second; the grid reloads once, while on screen, for the first.
   */
  stale: boolean
  /**
   * Bumped by every write the store learns of. A request records the
   * generation it started under; a response from an older generation is
   * thrown away, because it predates a write the rows already reflect
   * (utils/generation.ts).
   */
  generation: number
}

const initialState: AssignmentListState = {
  rows: [],
  isLoading: false,
  stale: false,
  generation: 0,
}

type Row = AssignmentListItem

/**
 * A state change moves the assignment to another template with the same
 * fields: the row's status flips and nothing else changes. Always a NEW array,
 * even when no row matched: the row may be in a read still in flight, and
 * withRows must move the generation so that read is dropped and taken again.
 */
const withStatus = (state: AssignmentListState, ids: string[], status: Row['status']) => {
  const flipped = new Set(ids)
  if (flipped.size === 0) return state
  return withRows(
    state,
    state.rows.map(row => (flipped.has(row.id) ? { ...row, status } : row)),
  )
}

/**
 * The Trade Closing grid's rows, as the server projects them (ADR_LOS-030).
 * Every write on an assignment is dispatched through the assignments duck —
 * by the row menu and by the assignment's own page — and this store listens
 * for the same successes and patches its rows. It never refetches after a
 * write: PQS projects asynchronously, so a read straight after the command
 * can still return the state the assignment just left.
 */
const assignmentListReducer = createReducer(initialState)({
  [types.GET_ASSIGNMENT_LIST]: (
    state: AssignmentListState,
    action: { payload: { rows: Row[]; generation?: number } },
  ) => {
    // Fetched before a write the store has since applied: these rows would
    // undo it. Drop them and leave the store stale; the thunk reads once more.
    if (outdated(state, action.payload.generation)) return { ...state, stale: true }
    return { ...state, rows: action.payload.rows, stale: false }
  },
  [types.INVALIDATE_ASSIGNMENT_LIST]: invalidate,
  [types.ASSIGNMENT_LIST_LOAD_FAILED]: (state: AssignmentListState) => ({ ...state, stale: false }),
  [types.START_LOADING_ASSIGNMENT_LIST]: (state: AssignmentListState) => ({
    ...state,
    isLoading: true,
  }),
  [types.STOP_LOADING_ASSIGNMENT_LIST]: (state: AssignmentListState) => ({
    ...state,
    isLoading: false,
  }),

  // The state changes answer `{ id, isSuccess, error }`.
  [assignmentTypes.SUBMIT_ASSIGNMENT]: (state: AssignmentListState, action: any) =>
    withStatus(state, oneId(action), 'Submitted'),
  [assignmentTypes.REJECT_ASSIGNMENT]: (state: AssignmentListState, action: any) =>
    withStatus(state, oneId(action), 'Draft'),
  [assignmentTypes.APPROVE_ASSIGNMENT]: (state: AssignmentListState, action: any) =>
    withStatus(state, oneId(action), 'Approved'),
  // The delete answers per id; a row the ledger refused to delete stays.
  [assignmentTypes.DELETE_ASSIGNMENT]: (state: AssignmentListState, action: any) => {
    const gone = new Set(acceptedIds(action.payload))
    return gone.size
      ? withRows(state, state.rows.filter(row => !gone.has(row.id)))
      : state
  },
  // A saved draft is not a grid row: the read resolves its deal, facility and
  // party names and sums its amount. The grid reads once more when next shown.
  [assignmentTypes.ADD_ASSIGNMENTS]: invalidate,
  [assignmentTypes.EDIT_ASSIGNMENTS]: invalidate,
})

export default assignmentListReducer
